/**
 * AiMessageBlocks — structured chatbot response renderers.
 *
 * Visual language reuses the existing in-flight reasoning demo patterns:
 *  - TagPill / ReasoningStepView / ReasoningSection  (timeline-dot + chips)
 *  - Glass-card containers (rgba(255,255,255,0.35) + border)
 *  - EMBER color tokens throughout
 */
import { useState, useEffect, type ReactNode } from "react";
import { useReducedMotion } from "@src/packages/moon-ui/hooks/useReducedMotion";
import {
  View,
  Text,
  Pressable,
  Linking,
  Image,
  type ImageSourcePropType,
} from "react-native";
import {
  Activity,
  BookOpen,
  ChevronRight,
  AlertTriangle,
  FileText,
  Pill,
  Stethoscope,
  Target,
  TestTube2,
  User,
  type LucideIcon,
} from "lucide-react-native";
import Animated, {
  FadeIn,
  useAnimatedStyle,
  useSharedValue,
  cancelAnimation,
  withRepeat,
  withSequence,
  withTiming,
} from "react-native-reanimated";
import {
  getBlock,
  getBlocksByKind,
  type ContentBlock,
  type ContentBlockId,
} from "@src/packages/content";
import { useTranslations } from "@src/packages/i18n";
import type {
  AiChatResponse,
  AiSource,
  SimpleAnswerBlock,
  ReasoningBlock,
  ReasoningCategory,
  ActionPlanBlock,
  ActionPlanItem,
  MissingDataQuestionsBlock,
  SafetyEscalationBlock,
} from "../hooks/useAiChat";
import { EMBER } from "../../onboarding/theme";
import { ThinkingOrbStatus } from "@src/components/ui/ThinkingOrbStatus";
import {
  CardSurface,
  Tag as MoonTag,
  type TagTone,
  semanticColors,
} from "@louise/moon-ui";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@louise/moon-ui/primitives/Accordion";
import { getLocalProductImage } from "./productImages";
import supplementBottle from "../../assets/images/supplement-bottle.png";
import supplementBottleTwo from "../../assets/images/supplement-bottle-2.png";
import supplementBottleThree from "../../assets/images/supplement-bottle-3.png";

import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import { withAlpha } from "@louise/theme/tokens";
/* ─── Exported types (shared with AiAssistantCard) ─── */

export type Tag = {
  label: string;
  tone?: TagTone;
  icon?: LucideIcon;
  kind?: string; // opens detail sheet on tap when both kind + blockId present
  blockId?: string;
};

export type ReasoningStep = {
  text: string;
  tags?: Tag[];
  isUpdating?: boolean;
};

/* ─── Color maps ─── */

const ITEM_STATUS_TONE: Record<string, TagTone> = {
  present: "chatNeutral",
  missing: "chatNeutral",
  uncertain: "chatNeutral",
};

/* ─── Modal registry ─────────────────────────────────────────────────────
 * Maps normalised label / name → content block ID.
 * To add a new mapping: normalise the key to lowercase, no extra spaces.
 * ─────────────────────────────────────────────────────────────────────── */

const MODAL_REGISTRY: Record<string, string> = {
  // ── Supplements ──────────────────────────────────────────────────────
  coq10: "supplement-coq10",
  ubiquinol: "supplement-coq10",
  "coenzyme q10": "supplement-coq10",
  "vitamin d": "supplement-vitamin-d",
  "vitamin d3": "supplement-vitamin-d",
  "omega 3": "supplement-omega-3",
  "omega-3": "supplement-omega-3",
  "fish oil": "supplement-omega-3",
  methylfolate: "supplement-methylfolate",
  "folic acid": "supplement-methylfolate",
  folate: "supplement-methylfolate",
  zinc: "supplement-zinc",
  "l-carnitine": "supplement-l-carnitine",
  "l carnitine": "supplement-l-carnitine",
  carnitine: "supplement-l-carnitine",
  selenium: "supplement-selenium",
  "myo-inositol": "supplement-myo-inositol",
  "myo inositol": "supplement-myo-inositol",
  inositol: "supplement-myo-inositol",
  "vitamin c": "supplement-vitamin-c",
  "ascorbic acid": "supplement-vitamin-c",
  dhea: "supplement-dhea",
  "vitamin e": "supplement-vitamin-e",
  nac: "supplement-nac",
  "n-acetyl cysteine": "supplement-nac",
  "n acetyl cysteine": "supplement-nac",
  melatonin: "supplement-melatonin",
  iron: "supplement-iron",
  probiotics: "supplement-probiotics",
  magnesium: "supplement-magnesium",
  ashwagandha: "supplement-ashwagandha",
  "vitamin b complex": "supplement-vitamin-b-complex",
  "b complex": "supplement-vitamin-b-complex",
  "l-arginine": "supplement-l-arginine",
  "l arginine": "supplement-l-arginine",
  arginine: "supplement-l-arginine",
  creatine: "supplement-creatine",
  "alpha lipoic acid": "supplement-alpha-lipoic-acid",
  "alpha-lipoic acid": "supplement-alpha-lipoic-acid",
  ala: "supplement-alpha-lipoic-acid",
  resveratrol: "supplement-resveratrol",
  curcumin: "supplement-curcumin",
  turmeric: "supplement-curcumin",
  berberine: "supplement-berberine",
  // ── Tests ─────────────────────────────────────────────────────────────
  amh: "test-amh",
  "amh blood test": "test-amh",
  "anti-mullerian hormone": "test-amh",
  "anti mullerian hormone": "test-amh",
  fsh: "test-general-bloodwork",
  "hormone panel": "test-general-bloodwork",
  "semen analysis": "test-semen-analysis",
  era: "test-endometrial-receptivity-testing-products-era",
  pgt: "test-preimplantation-genetic-testing",
  "preimplantation genetic testing": "test-preimplantation-genetic-testing",
  "pgt-a": "test-pgt-a",
  pgta: "test-pgt-a",
  "pgt-p": "test-pgt-p",
  pgtp: "test-pgt-p",
  "whole genome": "test-whole-genome",
  "fertility tracker": "test-fertility-tracker",
};

/**
 * Given an ActionPlanItem, return the content block to open as a modal,
 * or null if no match exists yet.
 */
export function resolveModalTarget(
  item: ActionPlanItem,
): { kind: string; blockId: string } | null {
  if (item.kind !== "supplement" && item.kind !== "test") return null;

  const block = resolveRecommendationBlock(item);
  return block ? { kind: block.kind, blockId: String(block.id) } : null;
}

type RecommendationContentBlock =
  ContentBlock<"supplement"> | ContentBlock<"test"> | ContentBlock<"product">;

function isRecommendationContentBlock(
  block: ContentBlock | undefined,
): block is RecommendationContentBlock {
  return (
    block?.kind === "supplement" ||
    block?.kind === "test" ||
    block?.kind === "product"
  );
}

function normalizeRecommendationName(value: string): string {
  return value
    .toLowerCase()
    .replace(/[+/_-]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function getRecommendationNames(block: RecommendationContentBlock): string[] {
  switch (block.kind) {
    case "supplement":
      return [block.title, block.body.name, ...(block.body.aliases ?? [])];
    case "test":
      return [block.title, block.body.testName];
    case "product":
      return [block.title, block.body.name];
  }
}

/** Resolve an AI item to the registered product/content block it represents. */
function resolveRecommendationBlock(
  item: ActionPlanItem,
): RecommendationContentBlock | null {
  const directBlock = getBlock(item.id as ContentBlockId);
  if (isRecommendationContentBlock(directBlock)) return directBlock;

  // Try card title, then label, then a cleaned-up item id. The AI contract
  // currently calls purchasable tests "test" items, while the catalog stores
  // their exact price/image under a "product-*" block, so name matching is
  // intentionally allowed across these three content kinds.
  const candidates = [
    item.card?.title,
    item.label,
    item.id
      .replace(/^(recommend_|discuss_|consider_|suggest_)/, "")
      .replace(/_/g, " "),
  ].filter((v): v is string => !!v && v.trim().length > 0);

  for (const candidate of candidates) {
    const key = normalizeRecommendationName(candidate);
    const legacyKey = candidate.toLowerCase().trim().replace(/\s+/g, " ");
    const blockId = MODAL_REGISTRY[key] ?? MODAL_REGISTRY[legacyKey];
    if (blockId) {
      const block = getBlock(blockId as ContentBlockId);
      if (isRecommendationContentBlock(block)) return block;
    }
  }

  const normalizedCandidates = candidates.map(normalizeRecommendationName);
  const catalogBlocks: RecommendationContentBlock[] = [
    ...getBlocksByKind("supplement"),
    ...getBlocksByKind("test"),
    ...getBlocksByKind("product"),
  ];
  const matchingBlock = catalogBlocks.find((candidateBlock) =>
    getRecommendationNames(candidateBlock).some((name) => {
      const normalizedName = normalizeRecommendationName(name);
      return normalizedCandidates.some(
        (candidate) =>
          candidate === normalizedName ||
          candidate.includes(normalizedName) ||
          normalizedName.includes(candidate),
      );
    }),
  );
  if (matchingBlock) return matchingBlock;

  // Last resort: auto-pattern {kind}-{id-slug}. This covers stable test and
  // supplement IDs while the backend and content catalog converge on naming.
  const autoIds = [item.kind, "product"]
    .map(
      (kind) =>
        `${kind}-${item.id
          .replace(/^(recommend_|discuss_|consider_|suggest_)/, "")
          .replace(/_/g, "-")
          .toLowerCase()}`,
    )
    .filter((id, index, ids) => ids.indexOf(id) === index);
  for (const autoId of autoIds) {
    const autoBlock = getBlock(autoId as ContentBlockId);
    if (isRecommendationContentBlock(autoBlock)) return autoBlock;
  }

  return null;
}

/* ─── Regex ─── */

const ENTITY_REF_REGEX = /(\{\{ref:([^:}]+):([^:}]+):([^}]+)\}\})/g;

const MINIMAL_CHAT_COLORS = {
  ink: semanticColors.text.primary,
  body: semanticColors.text.secondary,
  muted: semanticColors.text.tertiary,
  faint: semanticColors.text.faint,
  accent: semanticColors.accent.default,
} as const;

const REASONING_TIMELINE_COLOR = semanticColors.border.strong;
const REASONING_LEAD_COLOR = semanticColors.text.secondary;
const RECORD_CONTEXT_KEYS = new Set([
  "test_results",
  "biomarkers",
  "medical_history",
  "fertility_history",
  "medications",
  "treatment_context",
]);
const GOAL_CONTEXT_KEYS = new Set(["profile", "goals"]);
const BIOMARKER_CATEGORY_KEYS = new Set(["test_results", "biomarkers"]);

function reasoningTagIcon(categoryKey: string, label: string): LucideIcon {
  const normalizedLabel = label.toLowerCase();

  if (normalizedLabel.includes("goal")) return Target;
  if (normalizedLabel.includes("age")) return User;

  switch (categoryKey) {
    case "test_results":
      return TestTube2;
    case "biomarkers":
      return Activity;
    case "medications":
      return Pill;
    case "treatment_context":
      return Stethoscope;
    case "profile":
    case "goals":
      return Target;
    case "medical_history":
    case "fertility_history":
    default:
      return FileText;
  }
}

/* ─── TagPill ────────────────────────────────────────────────────────────
 * Reused for both in-flight reasoning animation AND API reasoning categories.
 * ─────────────────────────────────────────────────────────────────────── */

export function TagPill({
  tag,
  index,
  onPress,
  animate = true,
}: {
  tag: Tag;
  index: number;
  onPress?: (tag: Tag) => void;
  animate?: boolean;
}) {
  const capturePress = usePressCapture("AiTagPill");
  const isModalLink = !!(tag.kind && tag.blockId && onPress);

  return (
    <Animated.View
      entering={animate ? FadeIn.delay(index * 80).duration(280) : undefined}
      style={{ maxWidth: "100%", flexShrink: 1 }}
    >
      {isModalLink ? (
        <Pressable
          onPress={() => {
            capturePress(pressLabel(tag.label), "ai_tag_pill");
            onPress!(tag);
          }}
          accessibilityRole="button"
          accessibilityLabel={tag.label}
        >
          <MoonTag
            label={tag.label}
            tone={tag.tone ?? "chatNeutral"}
            shape="rounded"
            icon={tag.icon}
            trailing={
              <ChevronRight size={10} color={semanticColors.text.tertiary} />
            }
            textStyle={{ flexShrink: 1 }}
          />
        </Pressable>
      ) : (
        <MoonTag
          label={tag.label}
          tone={tag.tone ?? "chatNeutral"}
          shape="rounded"
          icon={tag.icon}
          textStyle={{ flexShrink: 1 }}
        />
      )}
    </Animated.View>
  );
}

/* ─── SourceChip ─── */

function SourceChip({
  source,
  index,
  animate = true,
}: {
  source: AiSource;
  index: number;
  animate?: boolean;
}) {
  const capturePress = usePressCapture("AiSourceChip");
  const canOpen = source.is_clickable && !!source.url;
  const year = source.year;
  const label = `${source.label}${year ? ` · ${year}` : ""}`;
  const sourceTag = (
    <MoonTag
      label={label}
      tone="chatNeutral"
      shape="rounded"
      icon={BookOpen}
      trailing={
        canOpen ? (
          <ChevronRight size={10} color={semanticColors.text.tertiary} />
        ) : null
      }
      textStyle={{ flexShrink: 1 }}
    />
  );

  return (
    <Animated.View
      entering={animate ? FadeIn.delay(index * 60).duration(280) : undefined}
      style={{ maxWidth: "100%", flexShrink: 1 }}
    >
      {canOpen ? (
        <Pressable
          onPress={() => {
            capturePress(pressLabel(label), "open_ai_source");
            void Linking.openURL(source.url!);
          }}
          accessibilityRole="button"
          accessibilityLabel={label}
        >
          {sourceTag}
        </Pressable>
      ) : (
        sourceTag
      )}
    </Animated.View>
  );
}

/* ─── ThinkingLabel (shimmer, used inside ReasoningSection) ─── */

export function ShimmerThinkingText({ text }: { text?: string } = {}) {
  const t = useTranslations();
  const reducedMotion = useReducedMotion();
  const shimmer = useSharedValue(reducedMotion ? 1 : 0.5);

  useEffect(() => {
    if (reducedMotion) {
      // The pulse is what makes the dimmed end of its range read as alive; a
      // label that merely stops mid-pulse is just permanently faded text.
      shimmer.value = 1;
      return;
    }
    shimmer.value = withRepeat(
      withSequence(
        withTiming(1, { duration: 1200 }),
        withTiming(0.4, { duration: 1200 }),
      ),
      -1,
      false,
    );
    return () => {
      cancelAnimation(shimmer);
    };
  }, [reducedMotion, shimmer]);

  const shimmerStyle = useAnimatedStyle(() => ({ opacity: shimmer.value }));

  return (
    <Animated.Text
      className="font-dm-sans"
      style={[
        {
          fontSize: 13,
          fontWeight: "500",
          color: EMBER.colors.textFaint,
          lineHeight: 18,
          flexShrink: 1,
        },
        shimmerStyle,
      ]}
    >
      {text?.trim() || t("learn.ai_message_blocks.thinking")}
    </Animated.Text>
  );
}

function ThinkingLabel() {
  return (
    <View
      style={{
        flexDirection: "row",
        alignItems: "center",
        gap: 5,
        marginBottom: 8,
      }}
    >
      <ChevronRight
        size={12}
        color={EMBER.colors.textFaint}
        strokeWidth={1.5}
      />
      <ShimmerThinkingText />
    </View>
  );
}

/* ─── ReasoningStepView ─── */

export function ReasoningStepView({
  step,
  index,
  isLast,
  onTagPress,
}: {
  step: ReasoningStep;
  index: number;
  isLast: boolean;
  onTagPress: (tag: Tag) => void;
}) {
  const hasTags = !!(step.tags && step.tags.length > 0);

  return (
    <Animated.View
      entering={FadeIn.delay(index * 100).duration(350)}
      style={{ flexDirection: "row" }}
    >
      {/* Timeline dot + connector */}
      <View style={{ alignItems: "center", width: 18, marginRight: 10 }}>
        <View
          style={{
            width: 7,
            height: 7,
            borderRadius: 4,
            backgroundColor: REASONING_TIMELINE_COLOR,
            borderWidth: 1.5,
            borderColor: REASONING_TIMELINE_COLOR,
            marginTop: 5,
          }}
        />
        {!isLast && (
          <View
            style={{
              flex: 1,
              width: 1,
              backgroundColor: REASONING_TIMELINE_COLOR,
              marginTop: 5,
              marginBottom: 4,
            }}
          />
        )}
      </View>

      {/* Content */}
      <View style={{ flex: 1, paddingBottom: isLast ? 0 : 14 }}>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 12,
            fontWeight: step.isUpdating ? "600" : "500",
            color: step.isUpdating
              ? EMBER.colors.textLight
              : EMBER.colors.textFaint,
            lineHeight: 17,
            marginBottom: hasTags ? 7 : 0,
            fontStyle: step.isUpdating ? "italic" : "normal",
          }}
        >
          {step.text}
        </Text>
        {hasTags && (
          <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 5 }}>
            {step.tags!.map((tag, ti) => (
              <TagPill
                key={`${tag.label}-${ti}`}
                tag={tag}
                index={ti}
                onPress={onTagPress}
              />
            ))}
          </View>
        )}
      </View>
    </Animated.View>
  );
}

/* ─── RichSummary (bold-aware summary text for ReasoningSection) ─── */

function RichSummary({
  text,
  collapsed,
}: {
  text: string;
  collapsed: boolean;
}) {
  const style = collapsed
    ? {
        fontSize: 11.5,
        color: EMBER.colors.textFaint,
        lineHeight: 16,
        marginTop: 2,
      }
    : {
        fontSize: 12.5,
        color: EMBER.colors.textLight,
        lineHeight: 18,
        marginTop: 8,
      };

  return (
    <Text className="font-dm-sans" style={style}>
      {text.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
        part.startsWith("**") && part.endsWith("**") ? (
          <Text key={i} style={{ fontWeight: "700", color: EMBER.colors.text }}>
            {part.slice(2, -2)}
          </Text>
        ) : (
          part
        ),
      )}
    </Text>
  );
}

/* ─── ReasoningSection ────────────────────────────────────────────────────
 * Used for BOTH the in-flight animation (defaultCollapsed=false, thinking=true)
 * and the API reasoning block (defaultCollapsed=true, thinking=false, summary=…).
 * ─────────────────────────────────────────────────────────────────────── */

export function ReasoningSection({
  thinking,
  steps,
  onTagPress,
  defaultCollapsed = false,
  summary,
  sources,
  label,
}: {
  thinking: boolean;
  steps: ReasoningStep[];
  onTagPress: (tag: Tag) => void;
  defaultCollapsed?: boolean;
  /** Short summary shown when section is collapsed */
  summary?: string;
  /** API reasoning sources — rendered as chips at the bottom when expanded */
  sources?: AiSource[];
  /** Override the header label (defaults to "Reasoning" / "Updating") */
  label?: string;
}) {
  const t = useTranslations();
  const capturePress = usePressCapture("AiReasoningSection");
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  const hasSteps = steps.length > 0;
  const hasSources = !!(sources && sources.length > 0);
  const isUpdating = hasSteps && steps.some((s) => s.isUpdating);

  if (!thinking && !hasSteps && !summary) return null;

  const resolvedLabel =
    label ??
    (isUpdating
      ? t("learn.ai_message_blocks.updating")
      : t("learn.ai_message_blocks.reasoning"));

  return (
    <View style={{ marginBottom: 8, paddingVertical: 4 }}>
      {/* Header */}
      <Pressable
        onPress={() => {
          if (!hasSteps) return;
          capturePress(
            pressLabel(
              thinking ? t("learn.ai_message_blocks.analyzing") : resolvedLabel,
            ),
            collapsed ? "expand_reasoning" : "collapse_reasoning",
          );
          setCollapsed((c) => !c);
        }}
        style={{
          flexDirection: "row",
          alignItems: "center",
          gap: 6,
          marginBottom: collapsed || (!hasSteps && thinking) ? 0 : 10,
        }}
      >
        {thinking && !hasSteps ? (
          <ThinkingLabel />
        ) : (
          <>
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 11,
                fontWeight: "600",
                color: EMBER.colors.textFaint,
                letterSpacing: 0.8,
                textTransform: "uppercase",
                flex: 1,
              }}
            >
              {thinking
                ? t("learn.ai_message_blocks.analyzing")
                : resolvedLabel}
            </Text>
            {hasSteps && (
              <ChevronRight
                size={12}
                color={EMBER.colors.textFaint}
                strokeWidth={2}
                style={{
                  transform: [{ rotate: collapsed ? "0deg" : "90deg" }],
                }}
              />
            )}
          </>
        )}
      </Pressable>

      {/* Summary — visible while collapsed, or expanded with no steps */}
      {summary && (collapsed || !hasSteps) && (
        <RichSummary text={summary} collapsed={collapsed} />
      )}

      {/* Steps — visible when expanded */}
      {!collapsed && hasSteps && (
        <View>
          {steps.map((step, j) => (
            <ReasoningStepView
              key={j}
              step={step}
              index={j}
              isLast={j === steps.length - 1 && !hasSources}
              onTagPress={onTagPress}
            />
          ))}

          {/* Sources sub-section */}
          {hasSources && (
            <View
              style={{
                marginTop: 10,
                paddingTop: 10,
              }}
            >
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 10,
                  fontWeight: "600",
                  color: EMBER.colors.textFaint,
                  letterSpacing: 0.7,
                  textTransform: "uppercase",
                  marginBottom: 7,
                }}
              >
                {t("learn.ai_message_blocks.sources")}
              </Text>
              <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 5 }}>
                {sources!.map((src, i) => (
                  <SourceChip key={src.id} source={src} index={i} />
                ))}
              </View>
            </View>
          )}
        </View>
      )}
    </View>
  );
}

/* ─── Text parsing helpers ─── */

function parseParagraph(text: string) {
  const segments: (
    | { type: "text"; value: string }
    | { type: "bold"; value: string }
    | { type: "entity"; kind: string; blockId: string; label: string }
  )[] = [];

  const parts = text.split(ENTITY_REF_REGEX);
  let i = 0;
  while (i < parts.length) {
    const part = parts[i]!;
    if (
      i + 4 < parts.length &&
      parts[i + 1] &&
      parts[i + 2] &&
      parts[i + 3] &&
      parts[i + 4]
    ) {
      if (part.length > 0) addTextWithBold(segments, part);
      segments.push({
        type: "entity",
        kind: parts[i + 2]!,
        blockId: parts[i + 3]!,
        label: parts[i + 4]!,
      });
      i += 5;
    } else {
      if (part.length > 0) addTextWithBold(segments, part);
      i++;
    }
  }
  return segments;
}

function addTextWithBold(
  segments: {
    type: string;
    value?: string;
    kind?: string;
    blockId?: string;
    label?: string;
  }[],
  text: string,
) {
  for (const bp of text.split(/(\*\*[^*]+\*\*)/g)) {
    if (bp.startsWith("**") && bp.endsWith("**")) {
      segments.push({ type: "bold", value: bp.slice(2, -2) });
    } else if (bp.length > 0) {
      segments.push({ type: "text", value: bp });
    }
  }
}

/* ─── EntityPill (inline entity ref — flows inside Text) ─── */

// A link inside a sentence keeps the sentence's colour; the underline alone
// marks it, whatever kind of entity it opens.
function EntityPill({
  label,
  onPress,
}: {
  label: string;
  onPress: () => void;
}) {
  return (
    <Text
      onPress={onPress}
      accessibilityRole="link"
      className="font-dm-sans"
      style={{
        fontSize: 13.5,
        fontWeight: "600",
        color: EMBER.colors.text,
        lineHeight: 20,
        textDecorationLine: "underline",
        textDecorationColor: semanticColors.border.strong,
      }}
    >
      {label}
    </Text>
  );
}

/* ─── AnswerView (rich text with bold + entity refs) ─── */

function AnswerView({
  text,
  onOpenEntity,
}: {
  text: string;
  onOpenEntity: (kind: string, blockId: string) => void;
}) {
  return (
    <View style={{ marginTop: 0, paddingTop: 6, gap: 8 }}>
      {text.split("\n\n").map((para, pi) => {
        const segments = parseParagraph(para);
        return (
          <Text
            key={pi}
            className="font-dm-sans"
            style={{
              fontSize: 13.5,
              color: MINIMAL_CHAT_COLORS.body,
              lineHeight: 20,
            }}
          >
            {segments.map((seg, si) => {
              if (seg.type === "entity") {
                return (
                  <EntityPill
                    key={si}
                    label={seg.label!}
                    onPress={() => onOpenEntity(seg.kind!, seg.blockId!)}
                  />
                );
              }
              if (seg.type === "bold") {
                return (
                  <Text
                    key={si}
                    style={{
                      fontWeight: "700",
                      color: MINIMAL_CHAT_COLORS.ink,
                    }}
                  >
                    {seg.value}
                  </Text>
                );
              }
              return seg.value ?? null;
            })}
          </Text>
        );
      })}
    </View>
  );
}

/* ─── Reasoning timeline (stepwise reveal) ────────────────────────────────
 * Atlal's design: a two-group vertical timeline rendered before the answer.
 *   ● lead narration  → data-category sub-steps (status-colored item chips)
 *   ● inter-step narration → sources sub-step ("Searching medical research")
 * Dots/connector sit on the first-person narration lines; categories and
 * sources render as gray-titled sub-steps grouped under the narration above.
 * Categories and sources append one-at-a-time over the stream
 * (block_item_appended), so each arrival re-renders and FadeIn gives the
 * progressive "step appears" feel — no streaming logic needed here.
 * ─────────────────────────────────────────────────────────────────────── */

/** One node on the reasoning timeline: dot + connector, with a first-person
 *  narration line and any number of sub-steps in its content column. */
function ReasoningTimelineNode({
  narration,
  index,
  isLast,
  emphasis = false,
  animate = true,
  children,
}: {
  narration: string;
  index: number;
  isLast: boolean;
  emphasis?: boolean;
  animate?: boolean;
  children?: ReactNode;
}) {
  return (
    <Animated.View
      entering={animate ? FadeIn.delay(index * 120).duration(350) : undefined}
      style={{ flexDirection: "row" }}
    >
      {/* Timeline dot + connector */}
      <View style={{ alignItems: "center", width: 18, marginRight: 10 }}>
        <View
          style={{
            width: 7,
            height: 7,
            borderRadius: 4,
            backgroundColor: REASONING_TIMELINE_COLOR,
            marginTop: 5,
          }}
        />
        {!isLast && (
          <View
            style={{
              flex: 1,
              width: 1,
              backgroundColor: REASONING_TIMELINE_COLOR,
              marginTop: 5,
              marginBottom: 4,
            }}
          />
        )}
      </View>

      {/* Content — minWidth:0 lets the column shrink below its content so
          chip rows wrap instead of overflowing the card (flexbox min-width
          auto gotcha, esp. on web). */}
      <View style={{ flex: 1, minWidth: 0, paddingBottom: isLast ? 0 : 8 }}>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: emphasis ? 13 : 12,
            fontWeight: "500",
            color: emphasis ? REASONING_LEAD_COLOR : MINIMAL_CHAT_COLORS.muted,
            lineHeight: emphasis ? 18 : 17,
            marginBottom: emphasis ? 6 : 5,
          }}
        >
          {narration}
        </Text>
        {children}
      </View>
    </Animated.View>
  );
}

/* reasoning — stepwise activity stream, default collapsed after completion */

export function ReasoningActivityView({
  block,
  complete = false,
  defaultCollapsed = false,
}: {
  block: ReasoningBlock;
  complete?: boolean;
  defaultCollapsed?: boolean;
}) {
  const t = useTranslations();
  const [collapsed, setCollapsed] = useState(defaultCollapsed || complete);

  useEffect(() => {
    if (complete) setCollapsed(true);
  }, [complete]);

  const categories = block.categories ?? [];
  const sources = block.sources ?? [];
  const hasSources = sources.length > 0;

  const leadNarration =
    block.summary?.trim() || t("learn.ai_message_blocks.lead_narration");

  // Hardcoded step titles keyed by category.key; t() returns the path
  // unchanged when a key is missing, so fall back to the API label.
  const resolveStepTitle = (cat: ReasoningCategory) => {
    const key = `learn.ai_message_blocks.steps.${cat.key}`;
    const title = t(key);
    return title === key
      ? cat.label || t("learn.ai_message_blocks.steps.other")
      : title;
  };

  const recordCategories = categories.filter((category) =>
    RECORD_CONTEXT_KEYS.has(category.key),
  );
  const goalCategories = categories.filter((category) =>
    GOAL_CONTEXT_KEYS.has(category.key),
  );
  const otherCategories = categories.filter(
    (category) =>
      !RECORD_CONTEXT_KEYS.has(category.key) &&
      !GOAL_CONTEXT_KEYS.has(category.key),
  );
  const categoryGroups = [
    ...(recordCategories.length > 0
      ? [
          {
            key: "records",
            narration: t("learn.ai_message_blocks.steps.records"),
            categories: recordCategories,
          },
        ]
      : []),
    ...(goalCategories.length > 0
      ? [
          {
            key: "goals",
            narration: t("learn.ai_message_blocks.steps.goals"),
            categories: goalCategories,
          },
        ]
      : []),
    ...otherCategories.map((category) => ({
      key: category.key,
      narration: resolveStepTitle(category),
      categories: [category],
    })),
  ];
  const hasActivityDetails = categoryGroups.length > 0 || hasSources;
  const hasLiveSummary = !complete && Boolean(block.summary?.trim());
  const timelineStepOffset = hasLiveSummary ? 0 : 1;
  const liveThinkingLabel =
    block.summary?.trim() || t("learn.ai_message_blocks.thinking");

  return (
    <View style={{ marginBottom: 4, paddingVertical: 2 }}>
      <Accordion
        type="multiple"
        value={collapsed ? [] : ["activity"]}
        onValueChange={(value: string[]) =>
          setCollapsed(!value.includes("activity"))
        }
      >
        <AccordionItem value="activity">
          {/* Header */}
          <AccordionTrigger
            testID="ai-chat-reasoning-toggle"
            accessibilityLabel={
              complete
                ? t("learn.ai_message_blocks.what_i_checked")
                : t("learn.ai_message_blocks.thinking")
            }
            action={{
              expand: "expand_activity",
              collapse: "collapse_activity",
            }}
            className="items-center justify-start gap-1.5 py-0"
          >
            <View
              style={{
                alignItems: "center",
                flexDirection: "row",
                gap: 8,
                flexShrink: 1,
                minWidth: 0,
              }}
            >
              {!complete && (
                <ThinkingOrbStatus
                  state="working"
                  size={20}
                  theme="light"
                  surface="none"
                  accessibilityLabel={t("learn.ai_message_blocks.thinking")}
                />
              )}
              {complete ? (
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 12,
                    fontWeight: "500",
                    color: MINIMAL_CHAT_COLORS.muted,
                  }}
                >
                  {t("learn.ai_message_blocks.what_i_checked")}
                </Text>
              ) : (
                <ShimmerThinkingText text={liveThinkingLabel} />
              )}
            </View>
          </AccordionTrigger>

          <AccordionContent className="pb-0">
            <View style={{ paddingTop: 10 }}>
              {hasActivityDetails ? (
                <View>
                  {/* First node explains the scope of the activity. */}
                  {!hasLiveSummary && (
                    <ReasoningTimelineNode
                      narration={leadNarration}
                      index={0}
                      isLast={categoryGroups.length === 0 && !hasSources}
                      emphasis
                      animate={!complete}
                    />
                  )}

                  {/* Each data group is its own visible activity step. */}
                  {categoryGroups.map((group, ci) => (
                    <ReasoningTimelineNode
                      key={group.key}
                      narration={group.narration}
                      index={ci + timelineStepOffset}
                      isLast={ci === categoryGroups.length - 1 && !hasSources}
                      animate={!complete}
                    >
                      <View
                        style={{
                          flexDirection: "row",
                          flexWrap: "wrap",
                          gap: 5,
                        }}
                      >
                        {group.categories.flatMap((category) =>
                          category.items.map((item, ti) => (
                            <TagPill
                              key={`${category.key}-${item.id}`}
                              index={ti}
                              animate={!complete}
                              tag={{
                                label: item.value
                                  ? `${item.label}: ${item.value}`
                                  : item.label,
                                tone:
                                  BIOMARKER_CATEGORY_KEYS.has(category.key) &&
                                  item.status === "present"
                                    ? "blue"
                                    : (ITEM_STATUS_TONE[item.status] ??
                                      "chatNeutral"),
                                icon: reasoningTagIcon(
                                  category.key,
                                  item.label,
                                ),
                              }}
                            />
                          )),
                        )}
                      </View>
                    </ReasoningTimelineNode>
                  ))}

                  {/* Final activity step records the research context. */}
                  {hasSources && (
                    <ReasoningTimelineNode
                      narration={t("learn.ai_message_blocks.sources_narration")}
                      index={categoryGroups.length + timelineStepOffset}
                      isLast
                      animate={!complete}
                    >
                      <View
                        style={{
                          flexDirection: "row",
                          flexWrap: "wrap",
                          gap: 5,
                        }}
                      >
                        {sources.map((src, i) => (
                          <SourceChip
                            key={src.id}
                            source={src}
                            index={i}
                            animate={!complete}
                          />
                        ))}
                      </View>
                    </ReasoningTimelineNode>
                  )}
                </View>
              ) : null}
            </View>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </View>
  );
}

/* ─── action_plan item card ───────────────────────────────────────────────
 * Tapping "Ask about this" (or a suggested chip) composes a question about the
 * item in the MAIN chat input with a quoted reference — no nested chat. For
 * supplement/test items, a separate ⓘ button opens the info detail sheet.
 * ─────────────────────────────────────────────────────────────────────── */

const SUPPLEMENT_BOTTLES: ImageSourcePropType[] = [
  supplementBottle,
  supplementBottleTwo,
  supplementBottleThree,
];

function getSupplementBottle(id: string): ImageSourcePropType {
  let hash = 0;
  for (let i = 0; i < id.length; i += 1) {
    hash = (hash << 5) - hash + id.charCodeAt(i);
    hash |= 0;
  }
  return SUPPLEMENT_BOTTLES[Math.abs(hash) % SUPPLEMENT_BOTTLES.length]!;
}

function formatCommercePrice(value: number, currency: string): string {
  const symbol = currency === "EUR" ? "€" : currency === "GBP" ? "£" : "$";
  const formatted = Number.isInteger(value)
    ? value.toFixed(0)
    : value.toFixed(2);
  return `${symbol}${formatted}`;
}

function suggestedContentIcon(kind: ActionPlanItem["kind"]): LucideIcon {
  switch (kind) {
    case "test":
      return TestTube2;
    case "supplement":
      return Pill;
    default:
      return FileText;
  }
}

type CommercePresentation = {
  block: RecommendationContentBlock;
  title: string;
  imageSource?: ImageSourcePropType;
  icon?: string;
  currentPrice?: string;
  originalPrice?: string;
  discountLabel?: string;
  supportingText?: string;
  orderUrl?: string;
};

function resolveCommercePresentation(
  item: ActionPlanItem,
): CommercePresentation | null {
  const block = resolveRecommendationBlock(item);
  if (!block || block.kind === "test") return null;

  if (block.kind === "product") {
    const originalPrice = block.body.originalPrice;
    return {
      block,
      title: block.body.name,
      imageSource:
        getLocalProductImage(String(block.id)) ??
        (block.body.imageUrl ? { uri: block.body.imageUrl } : undefined),
      icon: block.icon,
      currentPrice: formatCommercePrice(block.body.price, "USD"),
      originalPrice:
        originalPrice !== undefined
          ? formatCommercePrice(originalPrice, "USD")
          : undefined,
      discountLabel:
        originalPrice !== undefined && originalPrice > block.body.price
          ? `-${Math.round((1 - block.body.price / originalPrice) * 100)}%`
          : undefined,
      orderUrl: block.body.orderUrl,
    };
  }

  const purchase = block.body.purchase;
  const firstShopLink = block.body.shopLinks?.[0];
  const supportingText =
    block.body.expectations?.canExpect?.[0] ?? block.body.dosageRange;

  if (!purchase) {
    return {
      block,
      title: block.body.name,
      imageSource: getSupplementBottle(String(block.id)),
      icon: block.icon,
      supportingText,
      orderUrl: firstShopLink?.url,
    };
  }

  const originalPrice = purchase.originalPrice ?? purchase.retailPrice;
  return {
    block,
    title: block.body.name,
    imageSource: getSupplementBottle(String(block.id)),
    icon: block.icon,
    currentPrice: formatCommercePrice(purchase.price, purchase.currency),
    originalPrice:
      originalPrice !== undefined
        ? formatCommercePrice(originalPrice, purchase.currency)
        : undefined,
    discountLabel:
      purchase.discountLabel ??
      (originalPrice !== undefined && originalPrice > purchase.price
        ? `-${Math.round((1 - purchase.price / originalPrice) * 100)}%`
        : undefined),
    supportingText,
    orderUrl: purchase.orderUrl,
  };
}

function SuggestedContentRow({
  item,
  onOpenEntity,
}: {
  item: ActionPlanItem;
  onOpenEntity: (kind: string, blockId: string) => void;
}) {
  const capturePress = usePressCapture("AiSuggestedContentRow");
  const commerce = resolveCommercePresentation(item);
  const modalTarget = resolveModalTarget(item);
  const isStruck = item.status === "superseded" || item.status === "removed";
  const target =
    commerce?.block ??
    (modalTarget ? getBlock(modalTarget.blockId as ContentBlockId) : undefined);
  const fallbackImage =
    item.kind === "supplement"
      ? getSupplementBottle(String(item.id))
      : undefined;
  const Icon = suggestedContentIcon(item.kind);
  const title = commerce?.title ?? item.card?.title ?? item.label;
  const supportingText =
    item.card?.subtitle ??
    commerce?.supportingText ??
    item.card?.body ??
    item.recommendation_text;
  const openTarget = () => {
    if (target) {
      onOpenEntity(target.kind, String(target.id));
    }
  };

  return (
    <View style={{ marginTop: 10, opacity: isStruck ? 0.5 : 1 }}>
      <CardSurface
        variant="panel"
        style={{
          alignItems: "center",
          flexDirection: "row",
          gap: 12,
          paddingHorizontal: 14,
          paddingVertical: 12,
        }}
      >
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={title}
          disabled={!commerce?.orderUrl && !target}
          onPress={() => {
            capturePress(pressLabel(title), "open_suggested_content");
            openTarget();
          }}
          style={({ pressed }) => ({
            alignItems: "center",
            flex: 1,
            flexDirection: "row",
            gap: 12,
            opacity: pressed ? 0.6 : 1,
          })}
        >
          {(commerce?.imageSource ?? fallbackImage) ? (
            <Image
              source={commerce?.imageSource ?? fallbackImage!}
              resizeMode="contain"
              style={{ height: 44, width: 44 }}
            />
          ) : (
            <Icon
              color={MINIMAL_CHAT_COLORS.muted}
              size={24}
              strokeWidth={1.7}
            />
          )}
          <View style={{ flex: 1, minWidth: 0, gap: 4 }}>
            <Text
              className="font-dm-sans"
              numberOfLines={2}
              style={{
                color: MINIMAL_CHAT_COLORS.ink,
                fontSize: 14.5,
                fontWeight: "500",
                lineHeight: 18,
                textDecorationLine: isStruck ? "line-through" : "none",
              }}
            >
              {title}
            </Text>
            {commerce?.currentPrice ? (
              <View
                style={{ flexDirection: "row", alignItems: "center", gap: 7 }}
              >
                <Text
                  className="font-dm-sans"
                  style={{
                    color: MINIMAL_CHAT_COLORS.ink,
                    fontSize: 15,
                    fontWeight: "500",
                    textDecorationLine: isStruck ? "line-through" : "none",
                  }}
                >
                  {commerce.currentPrice}
                </Text>
                {commerce.discountLabel ? (
                  <Text
                    className="font-dm-sans"
                    style={{ color: EMBER.colors.coral, fontSize: 14 }}
                  >
                    {commerce.discountLabel}
                  </Text>
                ) : null}
                {commerce.originalPrice ? (
                  <Text
                    className="font-dm-sans"
                    style={{
                      color: MINIMAL_CHAT_COLORS.faint,
                      fontSize: 14,
                      textDecorationLine: "line-through",
                    }}
                  >
                    {commerce.originalPrice}
                  </Text>
                ) : null}
              </View>
            ) : (
              <Text
                className="font-dm-sans"
                numberOfLines={2}
                style={{
                  color: MINIMAL_CHAT_COLORS.muted,
                  fontSize: 11.5,
                  lineHeight: 16,
                }}
              >
                {supportingText}
              </Text>
            )}
          </View>
        </Pressable>
        {target ? (
          <ChevronRight
            color={MINIMAL_CHAT_COLORS.muted}
            size={18}
            strokeWidth={1.8}
          />
        ) : null}
      </CardSurface>
    </View>
  );
}

/* safety_escalation */

function SafetyEscalationBlockView({
  block,
}: {
  block: SafetyEscalationBlock;
}) {
  const isHigh = block.severity === "high";
  const accent = isHigh
    ? semanticColors.status.danger
    : semanticColors.status.warningText;

  return (
    <Animated.View
      entering={FadeIn.duration(400)}
      style={{
        marginTop: 4,
        backgroundColor: isHigh
          ? semanticColors.status.dangerTint
          : semanticColors.status.warningTint,
        borderRadius: 12,
        borderWidth: 1.5,
        borderColor: withAlpha(accent, 0.25),
        padding: 14,
      }}
    >
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          gap: 8,
          marginBottom: 8,
        }}
      >
        <AlertTriangle size={16} color={accent} strokeWidth={2} />
        <Text
          className="font-dm-sans"
          style={{ fontSize: 13, fontWeight: "700", color: accent, flex: 1 }}
        >
          {block.title}
        </Text>
      </View>
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 13.5,
          color: EMBER.colors.text,
          lineHeight: 20,
          marginBottom: block.reasons.length > 0 ? 10 : 0,
        }}
      >
        {block.content}
      </Text>
      {block.reasons.map((reason, i) => (
        <View key={i} style={{ flexDirection: "row", gap: 8, marginBottom: 4 }}>
          <Text style={{ color: accent, fontSize: 12, marginTop: 2 }}>•</Text>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 12.5,
              color: EMBER.colors.textLight,
              lineHeight: 18,
              flex: 1,
            }}
          >
            {reason}
          </Text>
        </View>
      ))}
    </Animated.View>
  );
}

function MissingDataPrompt({
  block,
  answerText,
}: {
  block: MissingDataQuestionsBlock;
  answerText: string;
}) {
  const normalizedAnswer = answerText.trim().toLowerCase();
  const question = block.questions.find(
    (candidate) =>
      candidate.question.trim().length > 0 &&
      !normalizedAnswer.includes(candidate.question.trim().toLowerCase()),
  );

  if (!question) return null;

  return (
    <View style={{ marginTop: 6 }}>
      <Text
        className="font-dm-sans"
        style={{
          color: MINIMAL_CHAT_COLORS.body,
          fontSize: 13.5,
          lineHeight: 20,
        }}
      >
        {question.question}
      </Text>
    </View>
  );
}

function SuggestedContentSection({
  block,
  onOpenEntity,
}: {
  block: ActionPlanBlock;
  onOpenEntity: (kind: string, blockId: string) => void;
}) {
  const t = useTranslations();
  const items = block.items
    .filter((item) => item.status === "active" || item.status === "updated")
    .filter((item) => item.kind === "test" || item.kind === "supplement");

  if (items.length === 0) return null;

  return (
    <View style={{ marginTop: 18 }}>
      <Text
        className="font-dm-sans"
        style={{
          color: MINIMAL_CHAT_COLORS.muted,
          fontSize: 12,
          lineHeight: 17,
          marginBottom: 2,
        }}
      >
        {t("learn.ai_message_blocks.suggested_for_question")}
      </Text>
      {items.map((item) => (
        <SuggestedContentRow
          key={`${item.kind}:${item.id}:${item.label}`}
          item={item}
          onOpenEntity={onOpenEntity}
        />
      ))}
    </View>
  );
}

/**
 * Projects the current API response into the conversational chat surface.
 * The API still streams its legacy blocks for compatibility, but the UI no
 * longer presents those blocks as separate answer sections.
 */
export function AiChatResponseView({
  response,
  onOpenEntity,
}: {
  response: AiChatResponse;
  onOpenEntity: (kind: string, blockId: string) => void;
}) {
  const answer = response.blocks.find(
    (block): block is SimpleAnswerBlock => block.type === "simple_answer",
  );
  const safety = response.blocks.find(
    (block): block is SafetyEscalationBlock =>
      block.type === "safety_escalation",
  );
  const actionPlan = response.blocks.find(
    (block): block is ActionPlanBlock => block.type === "action_plan",
  );
  const missingData = response.blocks.find(
    (block): block is MissingDataQuestionsBlock =>
      block.type === "missing_data_questions",
  );

  return (
    <View>
      {safety ? (
        <SafetyEscalationBlockView block={safety} />
      ) : answer ? (
        <AnswerView text={answer.content} onOpenEntity={onOpenEntity} />
      ) : null}
      {missingData ? (
        <MissingDataPrompt
          block={missingData}
          answerText={answer?.content ?? ""}
        />
      ) : null}
      {actionPlan ? (
        <SuggestedContentSection
          block={actionPlan}
          onOpenEntity={onOpenEntity}
        />
      ) : null}
    </View>
  );
}
